import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Search, FileText, Eye, RefreshCw, Receipt, ArrowRight,
  Package, Building2, CheckCircle, Clock, DollarSign, AlertTriangle
} from 'lucide-react';
import {
  getCompanies,
  getDeliveryNotesV2, markDNDelivered,
  createMaterialInvoice, getMaterialInvoicePending, getDNStats
} from '../lib/api';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  issued: 'bg-blue-100 text-blue-800',
  delivered: 'bg-green-100 text-green-800',
  acknowledged: 'bg-purple-100 text-purple-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800'
};

export const MaterialModulePage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [materialDNs, setMaterialDNs] = useState([]);
  const [invoicePending, setInvoicePending] = useState([]);
  const [invoicedDNs, setInvoicedDNs] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [viewDNOpen, setViewDNOpen] = useState(false);
  const [selectedDN, setSelectedDN] = useState(null);
  const [createInvoiceConfirm, setCreateInvoiceConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = selectedCompany !== 'all' ? selectedCompany : null;
      
      const [companiesRes, dnsRes, pendingRes, statsRes] = await Promise.all([
        getCompanies(),
        getDeliveryNotesV2({ company_id: companyId, dn_type: 'material' }),
        getMaterialInvoicePending(companyId),
        getDNStats(companyId)
      ]);
      
      setCompanies(companiesRes.data);
      setMaterialDNs(dnsRes.data || []);
      setInvoicePending(pendingRes.data || []);
      setInvoicedDNs((dnsRes.data || []).filter(dn => dn.invoice_id));
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkDelivered = async (dnId) => {
    try {
      const response = await markDNDelivered(dnId, {});
      toast.success('DN marked as delivered');
      
      if (response.data.invoice_pending) {
        toast.info('Added to invoice pending queue');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as delivered');
    }
  };

  const handleCreateInvoice = async (dn) => {
    try {
      const response = await createMaterialInvoice(dn.id);
      toast.success(`Invoice ${response.data.invoice_number} created successfully!`);
      setCreateInvoiceConfirm(false);
      setSelectedDN(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  const materialStats = {
    total: materialDNs.length,
    draft: materialDNs.filter(d => d.status === 'draft').length,
    delivered: materialDNs.filter(d => d.status === 'delivered' || d.status === 'acknowledged').length,
    pendingInvoice: invoicePending.length,
    invoiced: invoicedDNs.length,
    totalValue: materialDNs.reduce((sum, d) => sum + (d.total || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="material-module-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Material Module</h1>
          <p className="text-muted-foreground">Manage material deliveries and direct invoicing</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]" data-testid="company-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-2xl font-bold">{materialStats.total}</div>
                <p className="text-xs text-muted-foreground">Total Material DNs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <div>
                <div className="text-2xl font-bold">{materialStats.draft}</div>
                <p className="text-xs text-muted-foreground">Draft</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold">{materialStats.delivered}</div>
                <p className="text-xs text-muted-foreground">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              <div>
                <div className="text-2xl font-bold text-orange-700">{materialStats.pendingInvoice}</div>
                <p className="text-xs text-orange-600">Pending Invoice</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-purple-600" />
              <div>
                <div className="text-2xl font-bold">{materialStats.invoiced}</div>
                <p className="text-xs text-muted-foreground">Invoiced</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold">{materialStats.totalValue.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Total Value (SAR)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">
            <FileText className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pending-invoice">
            <Clock className="h-4 w-4 mr-2" />
            Pending Invoice ({invoicePending.length})
          </TabsTrigger>
          <TabsTrigger value="all-dns">
            <Package className="h-4 w-4 mr-2" />
            All Material DNs
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Material Workflow</CardTitle>
              <CardDescription>
                Material has a simpler workflow - Delivery Note goes directly to Invoice (no timesheet step)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8 gap-8">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-blue-100 rounded-full mb-2">
                    <FileText className="h-8 w-8 text-blue-600" />
                  </div>
                  <span className="font-medium">1. Client PO</span>
                  <span className="text-sm text-muted-foreground">Starting point</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-green-100 rounded-full mb-2">
                    <Package className="h-8 w-8 text-green-600" />
                  </div>
                  <span className="font-medium">2. Delivery Note</span>
                  <span className="text-sm text-muted-foreground">Material delivery</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-orange-100 rounded-full mb-2">
                    <Clock className="h-8 w-8 text-orange-600" />
                  </div>
                  <span className="font-medium">3. Invoice Pending</span>
                  <span className="text-sm text-muted-foreground">Auto-queued on delivery</span>
                </div>
                <ArrowRight className="h-6 w-6 text-muted-foreground" />
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-purple-100 rounded-full mb-2">
                    <Receipt className="h-8 w-8 text-purple-600" />
                  </div>
                  <span className="font-medium">4. Create Invoice</span>
                  <span className="text-sm text-muted-foreground">Direct from DN</span>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-2">Key Difference from Equipment/Manpower:</h4>
                <p className="text-muted-foreground">
                  Material deliveries go <span className="font-semibold text-orange-600">directly to invoice</span> without 
                  requiring a timesheet. When you mark a material DN as delivered, it automatically goes to the 
                  "Invoice Pending" queue where you can create the invoice with one click.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {invoicePending.length > 0 && (
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  Action Required: {invoicePending.length} DNs Pending Invoice
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {invoicePending.slice(0, 5).map(dn => (
                    <div key={dn.id} className="flex justify-between items-center p-3 border rounded hover:bg-muted/50">
                      <div>
                        <p className="font-medium">{dn.dn_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {dn.client_name} • {dn.currency} {dn.total?.toLocaleString()}
                        </p>
                      </div>
                      <Button 
                        onClick={() => {
                          setSelectedDN(dn);
                          setCreateInvoiceConfirm(true);
                        }}
                        data-testid={`create-invoice-${dn.id}`}
                      >
                        <Receipt className="h-4 w-4 mr-2" />
                        Create Invoice
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pending Invoice Tab */}
        <TabsContent value="pending-invoice" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Delivery Notes Pending Invoice</CardTitle>
              <CardDescription>
                These Material DNs have been delivered and are ready for invoicing
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : invoicePending.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>All caught up! No DNs pending invoice.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">DN Number</th>
                        <th className="pb-3 font-medium">Client</th>
                        <th className="pb-3 font-medium">PO Number</th>
                        <th className="pb-3 font-medium">Company</th>
                        <th className="pb-3 font-medium">Delivered</th>
                        <th className="pb-3 font-medium">Total</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoicePending.map(dn => (
                        <tr key={dn.id} className="border-b hover:bg-muted/50" data-testid={`pending-dn-${dn.id}`}>
                          <td className="py-3 font-medium">{dn.dn_number}</td>
                          <td className="py-3">{dn.client_name}</td>
                          <td className="py-3">{dn.client_po_number}</td>
                          <td className="py-3">{getCompanyName(dn.company_id)}</td>
                          <td className="py-3">{dn.delivered_at ? new Date(dn.delivered_at).toLocaleDateString() : '-'}</td>
                          <td className="py-3">{dn.currency} {dn.total?.toLocaleString()}</td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedDN(dn);
                                  setViewDNOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedDN(dn);
                                  setCreateInvoiceConfirm(true);
                                }}
                              >
                                <Receipt className="h-4 w-4 mr-1" />
                                Invoice
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All DNs Tab */}
        <TabsContent value="all-dns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Material Delivery Notes</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : materialDNs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No material delivery notes found. Create one from a Material Client PO.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">DN Number</th>
                        <th className="pb-3 font-medium">Client</th>
                        <th className="pb-3 font-medium">PO Number</th>
                        <th className="pb-3 font-medium">Company</th>
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium">Total</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Invoice</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialDNs.map(dn => (
                        <tr key={dn.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 font-medium">{dn.dn_number}</td>
                          <td className="py-3">{dn.client_name}</td>
                          <td className="py-3">{dn.client_po_number}</td>
                          <td className="py-3">{getCompanyName(dn.company_id)}</td>
                          <td className="py-3">{dn.delivery_date}</td>
                          <td className="py-3">{dn.currency} {dn.total?.toLocaleString()}</td>
                          <td className="py-3">
                            <Badge className={statusColors[dn.status]}>
                              {dn.status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            {dn.invoice_id ? (
                              <Badge className="bg-purple-100 text-purple-800">
                                <Receipt className="h-3 w-3 mr-1" />
                                Invoiced
                              </Badge>
                            ) : dn.invoice_pending ? (
                              <Badge className="bg-orange-100 text-orange-800">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedDN(dn);
                                  setViewDNOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {dn.status === 'draft' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkDelivered(dn.id)}
                                >
                                  Mark Delivered
                                </Button>
                              )}
                              {dn.invoice_pending && !dn.invoice_id && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedDN(dn);
                                    setCreateInvoiceConfirm(true);
                                  }}
                                >
                                  <Receipt className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View DN Dialog */}
      <Dialog open={viewDNOpen} onOpenChange={setViewDNOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Delivery Note Details</DialogTitle>
          </DialogHeader>
          
          {selectedDN && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">DN Number</Label>
                  <p className="font-medium">{selectedDN.dn_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className={statusColors[selectedDN.status]}>
                    {selectedDN.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="font-medium">{selectedDN.client_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Client PO</Label>
                  <p className="font-medium">{selectedDN.client_po_number}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Company</Label>
                  <p className="font-medium">{getCompanyName(selectedDN.company_id)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Delivery Date</Label>
                  <p className="font-medium">{selectedDN.delivery_date}</p>
                </div>
              </div>

              {selectedDN.items?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Items</Label>
                  <div className="mt-2 border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="p-2 text-left">Description</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-right">Rate</th>
                          <th className="p-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDN.items.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{item.description}</td>
                            <td className="p-2 text-right">{item.quantity} {item.unit}</td>
                            <td className="p-2 text-right">{item.rate?.toLocaleString()}</td>
                            <td className="p-2 text-right">{item.amount?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Subtotal</Label>
                  <p className="font-medium">{selectedDN.currency} {selectedDN.subtotal?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">VAT</Label>
                  <p className="font-medium">{selectedDN.currency} {selectedDN.vat_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total</Label>
                  <p className="font-bold">{selectedDN.currency} {selectedDN.total?.toLocaleString()}</p>
                </div>
              </div>

              {selectedDN.invoice_id && (
                <div className="p-3 bg-purple-50 rounded border border-purple-200">
                  <Label className="text-muted-foreground">Invoice Created</Label>
                  <p className="font-medium text-purple-700">Invoice ID: {selectedDN.invoice_id}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Confirmation Dialog */}
      <Dialog open={createInvoiceConfirm} onOpenChange={setCreateInvoiceConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice from Delivery Note</DialogTitle>
          </DialogHeader>
          
          {selectedDN && (
            <div className="space-y-4">
              <p>Are you sure you want to create an invoice for this delivery note?</p>
              
              <div className="p-4 bg-muted rounded">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">DN Number:</span>
                    <p className="font-medium">{selectedDN.dn_number}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Client:</span>
                    <p className="font-medium">{selectedDN.client_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total:</span>
                    <p className="font-bold">{selectedDN.currency} {selectedDN.total?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateInvoiceConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleCreateInvoice(selectedDN)} data-testid="confirm-create-invoice">
              <Receipt className="h-4 w-4 mr-2" />
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialModulePage;
